import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Avatar,
  Divider,
  Button,
  Chip,
  Stack,
} from "@mui/material";
import {
  Person,
  Work,
  Paid,
  EventAvailable,
  Description,
  ArrowBack,
} from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";

const API_URL = "https://api.hr-ebnhaian.cloud/api/v1/employees";
const BASE_UPLOAD =
  "https://api.hr-ebnhaian.cloud/uploads/employees";

/* =========================
   EXPIRY HELPER
========================= */
const getExpiryStatus = (expiryDate) => {
  if (!expiryDate) return { status: "NONE" };

  const today = new Date();
  const exp = new Date(expiryDate);

  const diffDays = Math.ceil(
    (exp - today) / (1000 * 60 * 60 * 24)
  );

  if (diffDays < 0) return { status: "EXPIRED", diffDays };
  if (diffDays <= 60) return { status: "EXPIRING", diffDays };

  return { status: "VALID", diffDays };
};

/* =========================
   DOCUMENT PATH HELPER
========================= */
const getDocumentUrl = (type, file) => {
  if (!file) return null;

  const base = `${BASE_UPLOAD}/documents/`;

  if (type === "passport") return `${base}passport/${file}`;
  if (type === "residence") return `${base}residence/${file}`;
  if (type === "visa") return `${base}visa/${file}`;

  return null;
};

/* =========================
   SECTION COMPONENT
========================= */
const Section = ({ title, icon, color = "#1976d2", children }) => (
  <Paper
    sx={{
      p: 3,
      mb: 3,
      borderLeft: `6px solid ${color}`,
      borderRadius: 2,
    }}
  >
    <Stack direction="row" spacing={1} alignItems="center" mb={2}>
      {icon}
      <Typography variant="h6" fontWeight="bold">
        {title}
      </Typography>
    </Stack>
    <Divider sx={{ mb: 2 }} />
    {children}
  </Paper>
);

function EmployeeDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);

  useEffect(() => {
    axios
      .get(`${API_URL}/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      })
      .then((res) => setEmployee(res.data.data))
      .catch(() => {
        alert("Employee not found");
        navigate("/employees");
      });
  }, [id, navigate]);

  if (!employee) {
    return <Typography p={3}>Loading...</Typography>;
  }

  const hasAlerts = ["passport", "residence", "visa"].some((doc) => {
    const d = employee.documents?.[doc];
    if (!d?.expiryDate) return false;
    return getExpiryStatus(d.expiryDate).status !== "VALID";
  });

  return (
    <Box p={3} sx={{ background: "#f4f6fa", minHeight: "100vh" }}>
      <Box height={70} />
      <Button
        startIcon={<ArrowBack />}
        variant="outlined"
        onClick={() => navigate(-1)}
        sx={{ mb: 3 }}
      >
        Back to Employees
      </Button>

      {hasAlerts && (
        <Paper
          sx={{
            p: 2,
            mb: 3,
            borderLeft: "6px solid #d32f2f",
            background: "#fdecea",
          }}
        >
          <Typography fontWeight="bold" color="error">
            ⚠️ This employee has expired or expiring documents
          </Typography>
        </Paper>
      )}

      {/* BASIC INFO */}
      <Section
        title="Basic Information"
        icon={<Person color="primary" />}
        color="#1976d2"
      >
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={3} textAlign="center">
            <Avatar
              src={
                employee.profileImage
                  ? `${BASE_UPLOAD}/profiles/${employee.profileImage}`
                  : undefined
              }
              sx={{
                width: 140,
                height: 140,
                mx: "auto",
                border: "4px solid #1976d2",
              }}
            >
              {!employee.profileImage &&
                employee.firstName?.charAt(0)}
            </Avatar>

            <Typography mt={1} fontWeight="bold">
              {employee.firstName} {employee.lastName}
            </Typography>
          </Grid>

          <Grid item xs={12} md={9}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography><b>Gender:</b> {employee.gender}</Typography>
                <Typography><b>Nationality:</b> {employee.nationality}</Typography>
                <Typography><b>Phone:</b> {employee.phone}</Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography><b>Email:</b> {employee.email || "-"}</Typography>
                <Typography><b>Employee Code:</b> {employee.employeeCode}</Typography>

                <Chip
                  label={employee.status}
                  color={employee.status === "ACTIVE" ? "success" : "error"}
                  sx={{ mt: 1 }}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Section>

      {/* JOB */}
      <Section
        title="Job Information"
        icon={<Work color="secondary" />}
        color="#9c27b0"
      >
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography><b>Center:</b> {employee.center?.name}</Typography>
            <Typography><b>Department:</b> {employee.jobRole}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography><b>Designation:</b> {employee.jobTitle}</Typography>
            <Typography>
              <b>Start Date:</b>{" "}
              {employee.startDate
                ? new Date(employee.startDate).toLocaleDateString()
                : "-"}
            </Typography>
          </Grid>
        </Grid>
      </Section>

      {/* SALARY */}
      <Section
        title="Salary & Payroll"
        icon={<Paid color="success" />}
        color="#2e7d32"
      >
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography><b>Basic Salary:</b> {employee.salary?.basicSalary}</Typography>
            <Typography><b>Payment Method:</b> {employee.salary?.paymentMethod}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography><b>Housing:</b> {employee.salary?.allowances?.housing}</Typography>
            <Typography><b>Transport:</b> {employee.salary?.allowances?.transport}</Typography>
            <Typography><b>Other:</b> {employee.salary?.allowances?.other}</Typography>
          </Grid>
        </Grid>
      </Section>

      {/* DOCUMENTS */}
      <Section
        title="Documents"
        icon={<Description color="info" />}
        color="#0288d1"
      >
        {["passport", "residence", "visa"].map((doc) => {
          const d = employee.documents?.[doc];
          if (!d) return null;

          const expiry = getExpiryStatus(d.expiryDate);

          return (
            <Paper key={doc} sx={{ p: 2, mb: 2 }}>
              <Typography fontWeight="bold">
                {doc.toUpperCase()}
              </Typography>

              <Typography>Number: {d.number}</Typography>

              <Typography>
                Expiry:{" "}
                {d.expiryDate
                  ? new Date(d.expiryDate).toLocaleDateString()
                  : "-"}
              </Typography>

              {d.file && (
                <Button
                  href={getDocumentUrl(doc, d.file)}
                  target="_blank"
                  size="small"
                  sx={{ mt: 1 }}
                >
                  View File
                </Button>
              )}
            </Paper>
          );
        })}
      </Section>
    </Box>
  );
}

export default EmployeeDetails;